import {
  EntityFlag,
  MatchState,
  Msg,
  decode,
  encodeFire,
  encodeHello,
  encodeInput,
  encodeThrow,
  type DamagedMsg,
  type DeathMsg,
  type ExplosionMsg,
  type HitConfirmMsg,
  type MatchMsg,
  type MatchSummaryMsg,
  type RosterEntry,
  type ShotMsg,
  type SnapshotEntity,
  type SnapshotProjectile,
} from '@fisura/protocol';
import { Transport, type NetSimConfig } from './transport.js';

/** Render delay for entity interpolation — render the world slightly in the past. */
const INTERP_DELAY_MS = 100;
const BUFFER_MS = 1000;

export interface RenderEntity {
  id: number;
  teamId: number;
  flags: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  vx: number;
  vy: number;
  vz: number;
  pitch: number;
  weaponId: number;
}

interface BufferedSnapshot {
  recvMs: number;
  entities: Map<number, SnapshotEntity>;
  projectiles: SnapshotProjectile[];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Shortest-path angular interpolation (radians). */
function lerpAngle(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return a + d * t;
}

/**
 * Client connection to the authoritative server. Sends input intents, buffers
 * snapshots, exposes time-interpolated state for remote entities and the latest
 * authoritative state for our own entity (for prediction/reconciliation).
 *
 * All traffic flows through a {@link Transport} that can simulate latency.
 */
export class NetClient {
  myId: number | null = null;
  myTeam = 0;
  tickHz = 30;
  snapHz = 20;
  ackSeq = 0;
  selfHealth = 100;
  selfAlive = true;
  selfReloading = false;
  selfSuppression = 0; // 0..1 from the snapshot
  selfBlind = 0; // 0..1 flashbang white-out, from the snapshot
  selfAmmo = 0; // authoritative rounds in the mag, from the snapshot
  /** Set when the server kicked us for inactivity — we then wait for a click. */
  idleKicked = false;
  readonly score = { team0: 0, team1: 0 };

  private readonly transport: Transport;
  private readonly snapshots: BufferedSnapshot[] = [];
  private reconData: { entity: SnapshotEntity; ackSeq: number } | null = null;
  private lastServerTick = -1;
  private readonly hitQueue: HitConfirmMsg[] = [];
  private readonly deathQueue: DeathMsg[] = [];
  private readonly damagedQueue: DamagedMsg[] = [];
  private readonly shotQueue: ShotMsg[] = [];
  private readonly explosionQueue: ExplosionMsg[] = [];
  private joinRequested = false;
  private wsOpen = false;
  private rejoined = false;
  private everJoined = false;

  /** Latest match state from the server (null until the first Match msg). */
  match: MatchMsg | null = null;

  /** Current wind direction (world yaw it blows toward); 0 until first Match msg. */
  get windDir(): number {
    return this.match?.windDir ?? 0;
  }
  /** Current wind speed in world units/second; 0 until first Match msg. */
  get windSpeed(): number {
    return this.match?.windSpeed ?? 0;
  }

  /** Set when the latest Match msg was a state transition (consumed by UI). */
  matchChanged = false;
  /** Full player roster (names + K/D) — kill feed + Tab scoreboard. */
  roster: RosterEntry[] = [];
  /** Frozen end-of-round leaderboard; set on match end, cleared at next start. */
  matchSummary: MatchSummaryMsg | null = null;
  private joinName = '';

  constructor(url: string, sim?: NetSimConfig) {
    this.transport = new Transport(
      url,
      (d) => this.handle(d),
      sim,
      () => this.onOpen(),
      (code) => this.onClose(code),
    );
  }

  /** True while we want to be BACK in the match but aren't yet. The first
   * join's brief Hello→Welcome window doesn't count — that's not an outage. */
  get reconnecting(): boolean {
    return this.joinRequested && !this.joined && this.everJoined;
  }

  /** Force an immediate reconnect attempt (skips the backoff). */
  retryNow(): void {
    this.transport.retryNow();
  }

  get connected(): boolean {
    return this.transport.connected;
  }

  /** True once we've been welcomed into the match. */
  get joined(): boolean {
    return this.myId !== null;
  }

  /**
   * Ask to enter the match. Connecting alone does NOT join (so idle menu tabs
   * never become standing players) — the server only spawns us on Hello.
   */
  join(name: string): void {
    if (this.joinRequested) return;
    this.joinRequested = true;
    this.idleKicked = false;
    this.joinName = name;
    if (this.wsOpen) this.transport.sendNow(encodeHello({ preferredTeam: 255, name }));
  }

  /** True exactly once after each Welcome (initial join AND every rejoin). */
  consumeRejoin(): boolean {
    const r = this.rejoined;
    this.rejoined = false;
    return r;
  }

  /** Display name for a player id (falls back to #id). */
  nameOf(id: number): string {
    return this.roster.find((p) => p.id === id)?.name ?? `#${id}`;
  }

  /** Most recent server tick we've heard about (for lag-comp render tick). */
  get serverTick(): number {
    return this.lastServerTick;
  }

  setSim(sim: NetSimConfig): void {
    this.transport.setSim(sim);
  }

  sendInput(seq: number, buttons: number, yaw: number, pitch: number, weaponId: number): void {
    this.transport.send(encodeInput({ seq, buttons, yaw, pitch, weaponId }));
  }

  sendFire(seq: number, renderTick: number, dx: number, dy: number, dz: number, weaponId: number): void {
    this.transport.send(encodeFire({ seq, renderTick, dx, dy, dz, weaponId }));
  }

  sendThrow(seq: number, dx: number, dy: number, dz: number, gtype: number): void {
    this.transport.send(encodeThrow({ seq, dx, dy, dz, gtype }));
  }

  /** Hit confirmations received since the last call (for hitmarkers). */
  consumeHits(): HitConfirmMsg[] {
    return this.hitQueue.splice(0, this.hitQueue.length);
  }

  /** Death events received since the last call (for kill feed / own death). */
  consumeDeaths(): DeathMsg[] {
    return this.deathQueue.splice(0, this.deathQueue.length);
  }

  /** Damage-taken events since the last call (for directional hit feedback). */
  consumeDamaged(): DamagedMsg[] {
    return this.damagedQueue.splice(0, this.damagedQueue.length);
  }

  /** Other players' shots since the last call (positional audio + tracers). */
  consumeShots(): ShotMsg[] {
    return this.shotQueue.splice(0, this.shotQueue.length);
  }

  /** Explosions since the last call (VFX + audio + shake). */
  consumeExplosions(): ExplosionMsg[] {
    return this.explosionQueue.splice(0, this.explosionQueue.length);
  }

  /** Interpolated projectile positions at render time. */
  sampleProjectiles(): SnapshotProjectile[] {
    const renderMs = performance.now() - INTERP_DELAY_MS;
    const snaps = this.snapshots;
    if (snaps.length === 0) return [];
    const last = snaps[snaps.length - 1]!;
    if (renderMs >= last.recvMs) return last.projectiles;
    let a = snaps[0]!;
    let b = last;
    for (let i = 0; i < snaps.length - 1; i++) {
      if (snaps[i]!.recvMs <= renderMs && renderMs < snaps[i + 1]!.recvMs) {
        a = snaps[i]!;
        b = snaps[i + 1]!;
        break;
      }
    }
    const span = b.recvMs - a.recvMs;
    const t = span > 0 ? Math.max(0, Math.min(1, (renderMs - a.recvMs) / span)) : 1;
    return b.projectiles.map((pb) => {
      const pa = a.projectiles.find((p) => p.id === pb.id);
      if (!pa) return pb;
      return {
        id: pb.id,
        kind: pb.kind,
        gtype: pb.gtype,
        x: lerp(pa.x, pb.x, t),
        y: lerp(pa.y, pb.y, t),
        z: lerp(pa.z, pb.z, t),
      };
    });
  }

  /** Interpolated entities at the current render time (server time minus delay). */
  sampleEntities(): RenderEntity[] {
    return this.sampleAt(performance.now() - INTERP_DELAY_MS);
  }

  /** Latest authoritative state for our own entity since the last call, or null. */
  consumeReconciliation(): { entity: SnapshotEntity; ackSeq: number } | null {
    const r = this.reconData;
    this.reconData = null;
    return r;
  }

  private onOpen(): void {
    this.wsOpen = true;
    // If the user already clicked play before the socket opened — or we are
    // auto-rejoining after a disconnect — join now. (sendNow bypasses the
    // latency sim so the Hello can't be dropped.)
    if (this.joinRequested) {
      this.transport.sendNow(encodeHello({ preferredTeam: 255, name: this.joinName }));
    }
  }

  /** Connection dropped: reset per-session state so the next Welcome is clean. */
  private onClose(code: number): void {
    this.wsOpen = false;
    this.myId = null;
    this.lastServerTick = -1; // a restarted server's ticks begin again at 0
    this.snapshots.length = 0;
    this.reconData = null;
    if (code === 4000) {
      // Kicked for inactivity: don't silently rejoin and idle again — the
      // overlay asks for a click instead. The socket itself still reconnects.
      this.joinRequested = false;
      this.idleKicked = true;
    }
  }

  private handle(data: ArrayBuffer): void {
    const msg = decode(data);
    if (!msg) return;

    if (msg.type === Msg.Welcome) {
      this.myId = msg.entityId;
      this.myTeam = msg.teamId;
      this.tickHz = msg.tickHz;
      this.snapHz = msg.snapHz;
      this.rejoined = true;
      this.everJoined = true;
      return;
    }

    if (msg.type === Msg.Score) {
      this.score.team0 = msg.team0;
      this.score.team1 = msg.team1;
      return;
    }

    if (msg.type === Msg.Match) {
      if (this.match && this.match.state !== msg.state) this.matchChanged = true;
      // A fresh match's countdown clears the stale results board.
      if (msg.state === MatchState.Starting) this.matchSummary = null;
      this.match = msg;
      return;
    }

    if (msg.type === Msg.MatchSummary) {
      this.matchSummary = msg;
      return;
    }

    if (msg.type === Msg.Roster) {
      this.roster = msg.players;
      return;
    }

    if (msg.type === Msg.Damaged) {
      this.damagedQueue.push(msg);
      return;
    }

    if (msg.type === Msg.Shot) {
      // Skip our own shots — local effects already rendered them.
      if (msg.shooterId !== this.myId) this.shotQueue.push(msg);
      return;
    }

    if (msg.type === Msg.Explosion) {
      this.explosionQueue.push(msg);
      return;
    }

    if (msg.type === Msg.HitConfirm) {
      this.hitQueue.push(msg);
      return;
    }

    if (msg.type === Msg.Death) {
      this.deathQueue.push(msg);
      return;
    }

    if (msg.type === Msg.Snapshot) {
      // Drop stale / out-of-order snapshots (jitter can reorder delivery).
      if (msg.serverTick <= this.lastServerTick) return;
      this.lastServerTick = msg.serverTick;
      this.ackSeq = msg.ackSeq;

      const map = new Map<number, SnapshotEntity>();
      for (const e of msg.entities) map.set(e.id, e);

      if (this.myId !== null) {
        const self = map.get(this.myId);
        if (self) {
          this.reconData = { entity: self, ackSeq: msg.ackSeq };
          this.selfHealth = self.health;
          this.selfAlive = (self.flags & EntityFlag.Alive) !== 0;
          this.selfReloading = (self.flags & EntityFlag.Reloading) !== 0;
          this.selfSuppression = self.suppression / 255;
          this.selfBlind = self.blind / 255;
          this.selfAmmo = self.ammo;
        }
      }

      const now = performance.now();
      this.snapshots.push({ recvMs: now, entities: map, projectiles: msg.projectiles });
      while (this.snapshots.length > 2 && this.snapshots[0]!.recvMs < now - BUFFER_MS) {
        this.snapshots.shift();
      }
    }
  }

  private sampleAt(renderMs: number): RenderEntity[] {
    const snaps = this.snapshots;
    if (snaps.length === 0) return [];

    if (renderMs <= snaps[0]!.recvMs) return entitiesOf(snaps[0]!);
    const last = snaps[snaps.length - 1]!;
    if (renderMs >= last.recvMs) return entitiesOf(last);

    let a = snaps[0]!;
    let b = last;
    for (let i = 0; i < snaps.length - 1; i++) {
      if (snaps[i]!.recvMs <= renderMs && renderMs < snaps[i + 1]!.recvMs) {
        a = snaps[i]!;
        b = snaps[i + 1]!;
        break;
      }
    }

    const span = b.recvMs - a.recvMs;
    const t = span > 0 ? (renderMs - a.recvMs) / span : 0;

    const out: RenderEntity[] = [];
    for (const [id, eb] of b.entities) {
      const ea = a.entities.get(id);
      if (!ea) {
        out.push({ ...eb });
        continue;
      }
      out.push({
        id,
        teamId: eb.teamId,
        flags: eb.flags,
        x: lerp(ea.x, eb.x, t),
        y: lerp(ea.y, eb.y, t),
        z: lerp(ea.z, eb.z, t),
        yaw: lerpAngle(ea.yaw, eb.yaw, t),
        vx: lerp(ea.vx, eb.vx, t),
        vy: lerp(ea.vy, eb.vy, t),
        vz: lerp(ea.vz, eb.vz, t),
        pitch: lerpAngle(ea.pitch, eb.pitch, t),
        weaponId: eb.weaponId,
      });
    }
    return out;
  }
}

function entitiesOf(s: BufferedSnapshot): RenderEntity[] {
  return [...s.entities.values()].map((e) => ({ ...e }));
}
